import { Navigate, useLocation } from 'react-router';
import type { ReactNode } from 'react';
import type { UserRole } from '@seenaly/shared';
import { useAuth } from './auth';
import { useT } from './prefs';
import { ForbiddenPage } from '@/pages/ForbiddenPage';

/**
 * Route guards.
 *
 * These control what the app *shows*. They are not the access control — the API
 * decides that (docs/spec.md §6). Typing a forbidden URL still gets a 403 from
 * the server even if a guard were wrong.
 */

export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();
  const t = useT();

  if (status === 'loading') {
    return <FullPageMessage>{t.common.loading}</FullPageMessage>;
  }

  if (status === 'anonymous') {
    // Remember where they were headed so login can send them back.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  return <>{children}</>;
}

export function RequireRole({
  allowed,
  children,
}: {
  allowed: readonly UserRole[];
  children: ReactNode;
}) {
  const { user } = useAuth();
  if (!user) return null;
  if (!allowed.includes(user.role)) return <ForbiddenPage />;
  return <>{children}</>;
}

function FullPageMessage({ children }: { children: ReactNode }) {
  return (
    <div
      style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', color: 'var(--sn-muted)' }}
    >
      {children}
    </div>
  );
}
